import React, { useMemo } from 'react';
import { CornerDownRight, ListChecks, Repeat } from 'lucide-react';
import { useTickets } from '../contexts/TicketsProvider';
import { PLANNING_GROUPS, planningGroup, formatDueDate, daysFromToday } from '../lib/dates';
import { TYPE_MAP, PRIORITY_MAP, RECURRENCE_MAP, isRecurring } from '../lib/constants';
import { Badge } from '../components/ui/badge';
import Spinner from '../components/Spinner';
import { cn } from '../lib/utils';

function Row({ ticket, onClick, stats }) {
  const type = TYPE_MAP[ticket.type];
  const priority = PRIORITY_MAP[ticket.priority];
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 text-left transition hover:border-primary/40 hover:shadow-sm"
    >
      <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', priority?.dot)} title={`Priority: ${priority?.label}`} />
      <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate text-sm font-medium">
        {ticket.parent ? (
          <span className="flex shrink-0 items-center gap-0.5 text-xs font-normal text-muted-foreground" title={`subtask of #${ticket.parent.id} ${ticket.parent.title}`}>
            <CornerDownRight className="h-3 w-3" />#{ticket.parent.id}
          </span>
        ) : null}
        <span className="truncate">{ticket.title}</span>
        {stats?.total ? (
          <span className="flex shrink-0 items-center gap-0.5 text-xs font-normal text-muted-foreground" title={`${stats.done} of ${stats.total} subtasks done`}>
            <ListChecks className="h-3 w-3" />{stats.done}/{stats.total}
          </span>
        ) : null}
      </span>
      <Badge className={cn('hidden shrink-0 sm:inline-flex', type?.badge)}>{type?.label}</Badge>
      <span className="hidden w-32 shrink-0 truncate text-xs text-muted-foreground md:block">
        {ticket.requestor?.name || 'Unassigned'}
      </span>
      <span className="flex w-28 shrink-0 items-center justify-end gap-1 text-xs text-muted-foreground">
        {isRecurring(ticket) ? (
          <Repeat className="h-3 w-3 shrink-0" title={`repeats: ${RECURRENCE_MAP[ticket.recurrence]?.label}`} />
        ) : null}
        {ticket.due_date ? formatDueDate(ticket.due_date) : '—'}
      </span>
    </button>
  );
}

export default function PlanningPage() {
  const { visibleTickets, openEditTicket, loading, subtaskStats } = useTickets();

  const groups = useMemo(() => {
    const open = visibleTickets.filter((t) => t.status !== 'done');
    const map = Object.fromEntries(PLANNING_GROUPS.map((g) => [g.key, []]));
    for (const t of open) map[planningGroup(t.due_date)].push(t);
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => {
        const da = daysFromToday(a.due_date);
        const db = daysFromToday(b.due_date);
        if (da !== db) {
          if (da === null) return 1;
          if (db === null) return -1;
          return da - db;
        }
        return (PRIORITY_MAP[a.priority]?.weight ?? 9) - (PRIORITY_MAP[b.priority]?.weight ?? 9);
      });
    }
    return map;
  }, [visibleTickets]);

  const totalOpen = visibleTickets.filter((t) => t.status !== 'done').length;

  return (
    <div className="p-4 sm:p-6">
      <p className="mb-4 text-sm text-muted-foreground">
        Your open work, organised by when it&apos;s due. {totalOpen} open ticket{totalOpen === 1 ? '' : 's'}.
      </p>

      {loading && visibleTickets.length === 0 ? (
        <Spinner />
      ) : totalOpen === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          Nothing open right now. 🎉
        </div>
      ) : (
        <div className="space-y-6">
          {PLANNING_GROUPS.map((group) => {
            const list = groups[group.key];
            if (!list.length) return null;
            const isOverdue = group.key === 'overdue';
            return (
              <section key={group.key}>
                <div className="mb-2 flex items-center gap-2">
                  <h2 className={cn('text-sm font-semibold', isOverdue && 'text-red-600 dark:text-red-400')}>
                    {group.label}
                  </h2>
                  <span className="rounded-full bg-muted px-1.5 text-xs text-muted-foreground">{list.length}</span>
                </div>
                <div className="space-y-2">
                  {list.map((ticket) => (
                    <Row key={ticket.id} ticket={ticket} stats={subtaskStats[ticket.id]} onClick={() => openEditTicket(ticket)} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
